import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod/v4";
import { api } from "@/lib/http";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingSpinner } from "@/components/loading";
import { generateDefaultValue } from "@/lib/utils/zod";

const contactUsSearchSchema = z.object({
  source: z.string().optional(),
});
export const Route = createFileRoute("/_app/contact-us")({
  component: RouteComponent,
  validateSearch: contactUsSearchSchema,
  staticData: {
    title: "Contact Us",
  },
});

const ContactUsFormSchema = z.object({
  full_name: z.string(),
  email: z.email(),
  company: z.string(),
  message: z.string(),
  phone_number: z
    .string()
    .max(15, "Phone number cannot be longer than 15 characters")
    .optional(),
  appeal_type: z.literal("I would like to Schedule a Demo"),
  source: z.string().optional(),
});
type ContactUsFormSchema = z.infer<typeof ContactUsFormSchema>;

function RouteComponent() {
  const { source } = Route.useSearch();
  const [isConsentChecked, setIsConsentChecked] = useState(false);

  const form = useForm({
    resolver: standardSchemaResolver(ContactUsFormSchema),
    defaultValues: {
      ...generateDefaultValue(ContactUsFormSchema),
      appeal_type: "I would like to Schedule a Demo",
      source,
    },
  });

  useEffect(() => {
    form.setValue("source", source);
  }, [source]);

  const submitContact = useMutation({
    async mutationFn(json: ContactUsFormSchema) {
      await api.auth.post("contact/", {
        json,
      });
    },
    onSuccess: () => toast.success("Request submitted successfully!"),
  });

  return (
    <div className="mx-auto my-auto max-h-100 max-w-200 justify-self-center-safe md:mt-10">
      <h1 className="mb-5 scroll-m-20 text-2xl font-bold tracking-tight lg:text-5xl">
        The page you tried to access is not available yet, but you can request a
        demo here:
      </h1>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((formData) =>
            submitContact.mutate(formData),
          )}
          className="*:my-2"
        >
          <FormField
            control={form.control}
            name="full_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>* Full Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>* Email</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone_number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone number</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Your Message</FormLabel>
                <FormControl>
                  <Textarea {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Consent Checkbox */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="consent"
              checked={isConsentChecked}
              onCheckedChange={(checked) =>
                setIsConsentChecked(checked === true)
              }
              className="mt-0.5 size-5 rounded-full border-gray-400 dark:border-gray-500"
            />
            <label
              htmlFor="consent"
              className="cursor-pointer text-sm leading-relaxed text-gray-600 dark:text-gray-300"
            >
              I agree to the use of my personal data in accordance with
              Polygraf Inc.'s{" "}
              <a
                href="https://polygraf.ai/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#ed2ea0] hover:underline"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="https://polygraf.ai/terms-and-conditions/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#ed2ea0] hover:underline"
              >
                Terms of Use
              </a>
              . Polygraf Inc. will not sell, trade, lease, or rent your personal
              data to third parties. This site is protected by reCAPTCHA and the{" "}
              {""}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#ed2ea0] hover:underline"
              >
                Google Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#ed2ea0] hover:underline"
              >
                Terms of Service
              </a>.
            </label>
          </div>

          <Button disabled={submitContact.isPending || !isConsentChecked} type="submit">
            {submitContact.isPending ? (
              <>
                <LoadingSpinner /> Submitting...
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
}
